import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { GraduationCap, Building2, School, ArrowRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function SignupPage() {
  const [step, setStep] = useState<'type' | 'details' | 'verification'>('type');
  const [userType, setUserType] = useState<'student' | 'landlord' | 'university'>('student');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    university: '',
    studentId: '',
    propertyCount: '',
    department: ''
  });
  const navigate = useNavigate();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('verification');
    // Simulate verification
    setTimeout(() => {
      navigate(`/dashboard/${userType}`);
    }, 2000);
  };
  const benefits = {
    student: ['Access to verified hostels only', 'AI-powered roommate matching', 'Secure escrow payments', '24/7 safety monitoring'],
    landlord: ['Reach verified students', 'Automated rent collection', 'Property verification badge', 'Tenant screening tools'],
    university: ['Track student locations', 'Safety monitoring dashboard', 'Ambassador management', 'Compliance reporting']
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1068" data-mp="p9x1hdvhvldl">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-1069" data-mp="kc58shn0zv8c">
        <Link to="/" className="flex justify-center items-center gap-2 mb-6" data-id="element-1070" data-mp="2jo7p5j0cs9hs">
          <img src="https://cdn.magicpatterns.com/uploads/37CjFFhCaYFTTZ1oinSNtZ/Cam_main_ver_logo.png" alt="CampusAparte" className="h-12 w-auto" data-id="element-1071" data-mp="2jm3nl7pxfmko" />
        </Link>
        <h2 className="text-center text-3xl font-bold tracking-tight text-brand-dark" data-id="element-1072" data-mp="cy4einoawrjq">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-slate-600" data-id="element-1073" data-mp="329pnathi08i">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-primary hover:text-brand-light" data-id="element-1074" data-mp="802yycq33ad0">
            Sign in
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl" data-id="element-1075" data-mp="10q1pkcjxffjs">
        <AnimatePresence mode="wait" data-id="element-1076" data-mp="10n5t6snq8yxg">
          {step === 'type' && <motion.div key="type" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-1077" data-mp="mp7etn9ep2hy">
              <Card data-id="element-1078" data-mp="33y216llppfa8">
                <CardContent className="p-8" data-id="element-1079" data-mp="33xitdxps079x">
                  <h3 className="text-xl font-bold text-brand-dark mb-6 text-center" data-id="element-1080" data-mp="33w7izf1ts19u">
                    Choose your account type
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-1081" data-mp="3dwhumv87p0mw">
                    {[{
                  id: 'student',
                  icon: GraduationCap,
                  label: 'Student/Parent',
                  desc: 'Find verified housing'
                }, {
                  id: 'landlord',
                  icon: Building2,
                  label: 'Landlord/Agent',
                  desc: 'List your property'
                }, {
                  id: 'university',
                  icon: School,
                  label: 'University Admin',
                  desc: 'Monitor students'
                }].map(type => <button key={type.id} onClick={() => setUserType(type.id as any)} className={`p-6 rounded-xl border-2 text-center transition-all ${userType === type.id ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20' : 'border-slate-200 hover:border-brand-primary/50 hover:bg-slate-50'}`} data-id="element-1082" data-mp="10sfsqglh5e8r">
                        <type.icon className={`w-8 h-8 mx-auto mb-3 ${userType === type.id ? 'text-brand-primary' : 'text-slate-400'}`} data-id="element-1083" data-mp="1jjfcn3e43xv6" />
                        <div className="font-bold text-brand-dark mb-1" data-id="element-1084" data-mp="1sfocgj604yhu">
                          {type.label}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1085" data-mp="3u9nm703h77sf">
                          {type.desc}
                        </div>
                      </button>)}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 mb-6" data-id="element-1086" data-mp="3rozdt8i1pwsv">
                    <h4 className="font-bold text-brand-dark mb-3" data-id="element-1087" data-mp="3mxzho5gr3irf">
                      What you'll get:
                    </h4>
                    <ul className="space-y-2" data-id="element-1088" data-mp="3hy4ti9od17a2">
                      {benefits[userType].map((benefit, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-slate-700" data-id="element-1089" data-mp="3d14jjnoe3kdo" data-mpi={idx}>
                          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-1090" data-mp="9itfh97ab69d" />
                          {benefit}
                        </li>)}
                    </ul>
                  </div>

                  <Button className="w-full" size="lg" onClick={() => setStep('details')} data-id="element-1091" data-mp="hmuamtox79s">
                    Continue as{' '}
                    {userType.charAt(0).toUpperCase() + userType.slice(1)}
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-1092" data-mp="2mpjbbhofv2j8" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>}

          {step === 'details' && <motion.div key="details" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} data-id="element-1093" data-mp="2cqlfdtooo6q7">
              <Card data-id="element-1094" data-mp="28s4dppo17og6">
                <CardContent className="p-8" data-id="element-1095" data-mp="1u1dompdtr3gr">
                  <div className="flex items-center justify-between mb-6" data-id="element-1096" data-mp="1u2ossqb6md8e">
                    <h3 className="text-xl font-bold text-brand-dark" data-id="element-1097" data-mp="1yzfafi2mxgvx">
                      Your details
                    </h3>
                    <button onClick={() => setStep('type')} className="text-sm text-slate-500 hover:text-brand-primary" data-id="element-1098" data-mp="1l0yzfexqp47z">
                      ← Back
                    </button>
                  </div>

                  <form className="space-y-4" onSubmit={handleSubmit} data-id="element-1099" data-mp="11ff8jmrgfu5v">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1100" data-mp="1fe6n1aa8bhuj">
                      <Input label="First Name" value={formData.firstName} onChange={e => setFormData({
                    ...formData,
                    firstName: e.target.value
                  })} required data-id="element-1101" data-mp="1ffq596fg42yy" />
                      <Input label="Last Name" value={formData.lastName} onChange={e => setFormData({
                    ...formData,
                    lastName: e.target.value
                  })} required data-id="element-1102" data-mp="1y70p53mr7fhc" />
                    </div>

                    <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} placeholder={userType === 'university' ? 'admin@university.edu.ng' : 'you@example.com'} required data-id="element-1103" data-mp="3ehimej6v7s2s" />

                    <Input label="Phone Number" type="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} placeholder="+234 800 000 0000" required data-id="element-1104" data-mp="35i5azkz6ktff" />

                    {userType === 'student' && <>
                        <Input label="University" value={formData.university} onChange={e => setFormData({
                    ...formData,
                    university: e.target.value
                  })} placeholder="e.g. University of Lagos" required data-id="element-1105" data-mp="1qkatpknn9npq" />
                        <Input label="Student ID (Optional)" value={formData.studentId} onChange={e => setFormData({
                    ...formData,
                    studentId: e.target.value
                  })} placeholder="Your student ID number" data-id="element-1106" data-mp="12uotctvjd2g5" />
                      </>}

                    {userType === 'landlord' && <Input label="Number of Properties" type="number" value={formData.propertyCount} onChange={e => setFormData({
                  ...formData,
                  propertyCount: e.target.value
                })} placeholder="How many properties do you manage?" required data-id="element-1107" data-mp="4xki58oot3xm" />}

                    {userType === 'university' && <>
                        <Input label="University Name" value={formData.university} onChange={e => setFormData({
                    ...formData,
                    university: e.target.value
                  })} placeholder="e.g. University of Lagos" required data-id="element-1108" data-mp="1534ps5xburgb" />
                        <Input label="Department" value={formData.department} onChange={e => setFormData({
                    ...formData,
                    department: e.target.value
                  })} placeholder="e.g. Student Affairs" required data-id="element-1109" data-mp="1sti8h5u0vdy8" />
                      </>}

                    <Input label="Password" type="password" value={formData.password} onChange={e => setFormData({
                  ...formData,
                  password: e.target.value
                })} placeholder="Min. 8 characters" required data-id="element-1110" data-mp="2lexe1ct2n154" />

                    <Input label="Confirm Password" type="password" value={formData.confirmPassword} onChange={e => setFormData({
                  ...formData,
                  confirmPassword: e.target.value
                })} placeholder="Re-enter your password" required data-id="element-1111" data-mp="1xpcm44exwtjp" />

                    <div className="pt-4" data-id="element-1112" data-mp="3qe3rpjddduan">
                      <label className="flex items-start gap-2 text-sm text-slate-600 mb-4" data-id="element-1113" data-mp="3vdq8g2sepso7">
                        <input type="checkbox" required className="mt-0.5 rounded border-slate-300 text-brand-primary focus:ring-brand-primary" data-id="element-1114" data-mp="47faifip1813" />
                        <span data-id="element-1115" data-mp="37pf9x6a6o533">
                          I agree to the{' '}
                          <a href="#" className="text-brand-primary hover:underline" data-id="element-1116" data-mp="3cmfhjlmolfwb">
                            Terms of Service
                          </a>{' '}
                          and{' '}
                          <a href="#" className="text-brand-primary hover:underline" data-id="element-1117" data-mp="2oxw6iyc8yp0j">
                            Privacy Policy
                          </a>
                        </span>
                      </label>

                      <Button type="submit" className="w-full" size="lg" data-id="element-1118" data-mp="24vrv5ctc7wdi">
                        Create Account
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>}

          {step === 'verification' && <motion.div key="verification" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} data-id="element-1119" data-mp="2sk2ta87nfhy3">
              <Card data-id="element-1120" data-mp="2xk79ry8k4cob">
                <CardContent className="p-12 text-center" data-id="element-1121" data-mp="2xjxk6brfc7j5">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.2,
                type: 'spring'
              }} className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1122" data-mp="2xicoujxackk8">
                    <Check className="w-10 h-10 text-emerald-600" data-id="element-1123" data-mp="3536ty63ue88q" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-brand-dark mb-2" data-id="element-1124" data-mp="3a1i1292r64n9">
                    Account Created!
                  </h3>
                  <p className="text-slate-600 mb-6" data-id="element-1125" data-mp="2w5ebit2tqkh4">
                    We've sent a verification email to{' '}
                    <strong data-id="element-1126" data-mp="2m53yb8gshd1y">{formData.email}</strong>
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-1127" data-mp="2m6yniyz3frbu">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary" data-id="element-1128" data-mp="2r61hnodjcd51"></div>
                    Redirecting to your dashboard...
                  </div>
                </CardContent>
              </Card>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}